'use client';

import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle
} from '@/components/ui/card';
import { useState } from 'react';
import { signIn } from 'next-auth/react'; // Client-side function from next-auth/react
import Link from 'next/link';
import { useRouter } from 'next/navigation';

export default function SignupPageClient({ originProduct = 'NOSSDR' }: { originProduct?: string }) {
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [error, setError] = useState('');
	const [success, setSuccess] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const router = useRouter();

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError('');
		setSuccess('');
		setIsLoading(true);

		try {
			const result = await signIn('CredentialsRegister', {
				email: email.toLowerCase(),
				password,
				redirect: false // Don't redirect automatically so we can handle the response
			});

			if (result?.error) {
				switch (result.error) {
					case 'CredentialsSignin':
						setError('Account creation failed. Email may already exist.');
						break;
					case 'AccessDenied':
						setError('Access denied. Please try again.');
						break;
					default:
						setError('An error occurred during sign up. Please try again.');
						break;
				}
			} else if (result?.ok) {
				// User was created successfully, now update their originProduct for tracking
				try {
					await fetch('/api/user/update-origin', {
						method: 'POST',
						headers: {
							'Content-Type': 'application/json',
						},
						body: JSON.stringify({
							email: email.toLowerCase(),
							originProduct
						}),
					});
				} catch (originError) {
					// Don't fail the signup if we can't update the origin
					console.warn('Failed to set origin product:', originError);
				}

				setSuccess('Account created successfully! Redirecting...');
				// Hard redirect to stay on the same domain
				window.location.href = '/';
			}
		} catch (err) {
			console.error('Signup error:', err);
			setError('An unexpected error occurred. Please try again.');
		} finally {
			setIsLoading(false);
		}
	}; return (
		<>
			<div className="min-h-screen flex justify-center items-start md:items-center p-8">
				<Card className="w-full max-w-sm space-y-4">
					<CardHeader>
						<CardTitle className="text-2xl">Sign Up</CardTitle>
						<CardDescription>Create your account</CardDescription>
					</CardHeader>
					<CardContent className="space-y-4">
						{/* Email and Password Sign Up */}
						<form onSubmit={handleSubmit} className="space-y-4">
							<input
								type="email"
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								placeholder="Email"
								className="w-full h-10 px-3 mb-2 border border-gray-300 rounded focus:outline-none focus:border-blue-500"
								required
							/>
							<input
								type="password"
								value={password}
								onChange={(e) => setPassword(e.target.value)}
								placeholder="Password"
								className="w-full h-10 px-3 mb-2 border border-gray-300 rounded focus:outline-none focus:border-blue-500"
								required
							/>
							{error && <p className="text-red-500 text-sm">{error}</p>}
							{success && <p className="text-green-500 text-sm">{success}</p>}
							<button
								type="submit"
								disabled={isLoading}
								className="w-full h-10 bg-blue-500 hover:bg-blue-600 disabled:bg-blue-400 disabled:cursor-not-allowed text-white font-bold rounded transition-colors flex items-center justify-center"
							>
								{isLoading ? (
									<>
										<svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
											<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
											<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
										</svg>
										Creating Account...
									</>
								) : (
									'Sign Up'
								)}
							</button>
						</form>
					</CardContent>

					<div className="flex items-center my-2">
						<hr className="flex-grow border-gray-300" />
						<span className="px-2 text-gray-500">OR</span>
						<hr className="flex-grow border-gray-300" />
					</div>

					<CardContent className="space-y-4">
						{/* Google Sign-Up */}
						<form
							onSubmit={async (e) => {
								e.preventDefault();
								setError('');

								try {
									const result = await signIn('google', {
										redirect: false // Don't redirect automatically
									});

									if (result?.error) {
										setError(
											'Failed to sign up with Google. Please try again.'
										);
									} else if (result?.ok) {
										setSuccess('Account created successfully! Redirecting...');
										// Hard redirect to stay on the same domain
										window.location.href = '/';
									}
								} catch (err) {
									console.error('Google sign-up error:', err);
									setError(
										'An error occurred with Google sign-up. Please try again.'
									);
								}
							}}
						>
							<button
								type="submit"
								className="w-full h-10 bg-white hover:bg-gray-50 text-gray-800 font-bold py-2 px-4 border border-gray-300 rounded shadow inline-flex items-center justify-center transition-colors"
							>
								<svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
									<path
										fill="#4285F4"
										d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
									/>
									<path
										fill="#34A853"
										d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
									/>
									<path
										fill="#FBBC05"
										d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
									/>
									<path
										fill="#EA4335"
										d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
									/>
									<path fill="none" d="M1 1h22v22H1z" />
								</svg>
								Sign Up with Google
							</button>
						</form>
					</CardContent>

					<CardFooter>
						<p className="text-xs text-center text-gray-600 mt-4 w-full">
							Already have an account?{' '}
							<Link
								href="/login" // Link to your login page
								className="text-blue-600 hover:underline"
							>
								Log in here
							</Link>
						</p>
						<p className="text-xs text-center text-gray-600 mt-4 w-full">
							By signing up, you agree to our{' '}
							<Link href="/terms" className="text-blue-600 hover:underline">
								Terms of Service
							</Link>{' '}
							and{' '}
							<Link href="/privacy" className="text-blue-600 hover:underline">
								Privacy Policy
							</Link>
							.
						</p>
					</CardFooter>
				</Card>
			</div>
		</>
	);
}
